import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../contextapi/UserContext';
import { supabase } from '../../utils/supabaseClient';
import { toast } from '../../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { validateInvite, respondToInvite, type InviteValidation } from '../../services/inviteService';

const checkUserOnboardingStatus = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('get_user_profile', { user_id: userId });
    if (error) return true;
    return data?.[0]?.is_onboarded ?? false;
  } catch {
    return true;
  }
};

export function InviteLinkHandler() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  const [inviteData, setInviteData] = useState<InviteValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setError('No invite code provided');
      setLoading(false);
      return;
    }

    validateInvite(code)
      .then(async (data) => {
        setInviteData(data);

        // Org invite + logged-in user → auto-accept immediately
        if (user && data.invite_type === 'organisation') {
          await performAction('accept', data);
          return;
        }

        // Personal invite + not logged in → redirect to signup (code travels via URL param)
        if (!user && data.invite_type === 'personal') {
          navigate(`/signup?invite=${code}`);
          return; // keep loading so spinner shows during redirect
        }

        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Invalid invite link');
        setLoading(false);
      });
  }, [code]);

  const performAction = async (action: 'accept' | 'decline', data?: InviteValidation) => {
    const invite = data ?? inviteData;
    if (!invite || !code) return;

    setActing(true);
    try {
      const result = await respondToInvite(code, action);

      if (action === 'decline') {
        toast({ title: 'Declined', description: 'You have declined the invite.' });
        navigate('/');
        return;
      }

      toast({ title: 'Success', description: 'Invite accepted!' });

      const isOnboarded = user?.id ? await checkUserOnboardingStatus(user.id) : false;

      if (!isOnboarded) {
        navigate('/onboarding');
        return;
      }

      if (invite.invite_type === 'organisation' && result.tenant_id) {
        const { data: orgData } = await supabase
          .from('base_tenants')
          .select('tenant_name')
          .eq('tenant_id', result.tenant_id)
          .single();

        navigate('/dashboard', {
          state: {
            joinedOrganisation: true,
            organisationName: (orgData as any)?.tenant_name ?? invite.tenant_name ?? 'the organisation',
          },
        });
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to process invite';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      setLoading(false);
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Processing Invite</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Invite</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!inviteData) return null;

  // Personal invite + logged-in user: can't accept (personal invites are for new users)
  if (user && inviteData.invite_type === 'personal') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Personal Invite</CardTitle>
            <CardDescription>
              This invite is for a new account at <strong>{inviteData.receiver_email}</strong>.
              You're already signed in as a different user.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not logged in: show invite card with signup/login options
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>You're Invited!</CardTitle>
            <CardDescription>
              <strong>{inviteData.sender_name}</strong> invited you to join
              {inviteData.tenant_name ? <> <strong>{inviteData.tenant_name}</strong></> : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-1">
              <p><span className="font-medium">Type:</span> {inviteData.invite_type === 'personal' ? 'Personal' : 'Organisation'}</p>
              <p><span className="font-medium">For:</span> {inviteData.receiver_email}</p>
            </div>

            {!inviteData.receiver_exists && (
              <Button onClick={() => navigate(`/signup?invite=${code}`)} className="w-full">
                Create Account to Accept
              </Button>
            )}
            {inviteData.receiver_exists && (
              <Button onClick={() => navigate(`/login?invite=${code}`)} className="w-full">
                Log In to Accept
              </Button>
            )}
            <Button variant="ghost" onClick={() => navigate('/')} className="w-full">
              Decline
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged-in user with an org invite that wasn't auto-accepted (edge case fallback)
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Accept Invite</CardTitle>
          <CardDescription>
            <strong>{inviteData.sender_name}</strong> invited you to join
            {inviteData.tenant_name ? <> <strong>{inviteData.tenant_name}</strong></> : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground space-y-1">
            <p><span className="font-medium">Type:</span> {inviteData.invite_type === 'personal' ? 'Personal' : 'Organisation'}</p>
            <p><span className="font-medium">For:</span> {inviteData.receiver_email}</p>
          </div>
          <Button
            onClick={() => performAction('accept')}
            disabled={acting}
            className="w-full gap-2"
          >
            {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Accept Invite
          </Button>
          <Button
            variant="outline"
            onClick={() => performAction('decline')}
            disabled={acting}
            className="w-full gap-2"
          >
            <XCircle className="h-4 w-4" />
            Decline
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
